/**
 * Client-side evaluation of Uni Forms calculation questions.
 *
 * Mirrors ``uni_forms/calculations.py``: left-to-right + − × ÷ over an
 * unbounded list of source questions.  Structured ``calculation_terms``
 * win; a legacy ``calculation_formula`` of ``{{question_id}}`` is the
 * fallback.
 *
 * Editable vs non-editable is a display concern.  Live recompute uses
 * per-question source fingerprints so typing into an editable result
 * is kept until a *source* answer changes, at which point the result
 * is overwritten (matching the product rule).
 */

export const CALCULATION_OPS = [
  { value: '+', label: 'Add (+)' },
  { value: '-', label: 'Subtract (−)' },
  { value: '*', label: 'Multiply (×)' },
  { value: '/', label: 'Divide (÷)' },
];

/** Types that coerce cleanly to a number without extra parsing. */
export const CALCULATION_SOURCE_TYPES = [
  'number', 'rating', 'slider', 'linear_scale', 'calculation',
];

/** Layout / secret types the Calculate-from dropdown must never offer. */
export const CALCULATION_UNSUPPORTED_SOURCE_TYPES = [
  'section_header', 'page_break', 'html',
  'respondent_identity',
  'password', 'password_confirm',
  'stripe', 'paypal',
  'hidden',
];

export function isCalculationSourceType(questionType) {
  return !CALCULATION_UNSUPPORTED_SOURCE_TYPES.includes(questionType);
}

const PLACEHOLDER = /\{\{([\w-]+)\}\}/g;
const SAFE_ARITHMETIC = /^[\d\s+\-*/().,%]*$/;

export function emptyCalculationTerms() {
  return [{ question_id: '' }, { op: '+', question_id: '' }];
}

export function coerceNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (Array.isArray(value)) {
    if (!value.length) return null;
    const parsed = value.map(coerceNumber);
    if (parsed.every((n) => n !== null)) {
      return parsed.reduce((a, b) => a + b, 0);
    }
    return value.filter((item) => item !== null && item !== '').length;
  }
  const text = String(value).trim().replace(/,/g, '');
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

function numberOrZero(value) {
  const n = coerceNumber(value);
  return n === null ? 0 : n;
}

function applyOp(left, op, right) {
  if (op === '+') return left + right;
  if (op === '-') return left - right;
  if (op === '*') return left * right;
  if (op === '/') {
    if (right === 0) return null;
    return left / right;
  }
  return null;
}

export function termQuestionIds(question) {
  const terms = question?.calculation_terms || [];
  const ids = terms
    .map((t) => (t && t.question_id ? String(t.question_id) : ''))
    .filter(Boolean);
  if (ids.length) return ids;
  const formula = question?.calculation_formula || '';
  const found = [];
  String(formula).replace(PLACEHOLDER, (_, id) => {
    found.push(id);
    return _;
  });
  return found;
}

function evaluateTerms(terms, answers) {
  if (!terms || !terms.length) return null;
  let acc = numberOrZero(answers[String(terms[0].question_id || '')]);
  for (let i = 1; i < terms.length; i += 1) {
    const term = terms[i] || {};
    const right = numberOrZero(answers[String(term.question_id || '')]);
    acc = applyOp(acc, term.op, right);
    if (acc === null) return null;
  }
  return acc;
}

function evaluateFormula(formula, answers) {
  const src = String(formula || '').trim();
  if (!src) return null;
  const expr = src.replace(PLACEHOLDER, (_, id) => String(numberOrZero(answers[id])));
  if (!SAFE_ARITHMETIC.test(expr) || !expr.trim()) return null;
  try {
    // Whitelisted character class above — same contract as the Python evaluator.
    // eslint-disable-next-line no-new-func
    const value = Function(`"use strict"; return (${expr});`)();
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function decimalsFor(question) {
  const n = Number(question?.calculation_decimals);
  if (!Number.isFinite(n)) return 2;
  return Math.max(0, Math.min(8, Math.trunc(n)));
}

export function formatCalculated(value, question) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '';
  }
  const decimals = decimalsFor(question);
  const rounded = Number(Number(value).toFixed(decimals));
  if (decimals === 0) return String(Math.trunc(rounded));
  let text = rounded.toFixed(decimals);
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '');
  return text || '0';
}

export function evaluateQuestion(question, answers) {
  const terms = question?.calculation_terms || [];
  const value = terms.length
    ? evaluateTerms(terms, answers || {})
    : evaluateFormula(question?.calculation_formula || '', answers || {});
  if (value === null || !Number.isFinite(Number(value))) return null;
  const decimals = decimalsFor(question);
  return Number(Number(value).toFixed(decimals));
}

export function orderCalculations(questions) {
  const calcs = (questions || []).filter((q) => q.question_type === 'calculation');
  if (!calcs.length) return [];
  const byId = {};
  calcs.forEach((q) => { byId[String(q.id)] = q; });
  const calcIds = new Set(Object.keys(byId));
  const deps = {};
  calcs.forEach((q) => {
    const refs = new Set(
      termQuestionIds(q).filter((id) => calcIds.has(String(id)) && String(id) !== String(q.id)),
    );
    deps[String(q.id)] = refs;
  });
  const ordered = [];
  const remaining = new Set(Object.keys(byId));
  while (remaining.size) {
    const ready = [...remaining].filter((id) => {
      for (const dep of deps[id]) {
        if (remaining.has(dep)) return false;
      }
      return true;
    });
    if (!ready.length) {
      remaining.forEach((id) => ordered.push(byId[id]));
      break;
    }
    ready.sort((a, b) => (byId[a].order || 0) - (byId[b].order || 0));
    ready.forEach((id) => {
      ordered.push(byId[id]);
      remaining.delete(id);
    });
  }
  return ordered;
}

function stringifyAnswer(value) {
  try {
    return JSON.stringify(value ?? '');
  } catch {
    return String(value ?? '');
  }
}

export function sourcesFingerprint(question, answers) {
  return termQuestionIds(question)
    .map((id) => `${id}=${stringifyAnswer(answers?.[id])}`)
    .join('|');
}

export const CALC_RESET_WEEKDAYS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

export const CALC_RESET_MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export function ordinalDay(day) {
  const value = Number(day) || 1;
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
  switch (value % 10) {
    case 1: return `${value}st`;
    case 2: return `${value}nd`;
    case 3: return `${value}rd`;
    default: return `${value}th`;
  }
}

export function monthDayOptions() {
  const options = [];
  for (let day = 1; day <= 31; day += 1) {
    options.push({ value: String(day), label: ordinalDay(day) });
  }
  return options;
}

export function calculationResetScheduleLabel(question) {
  if (!question?.calculation_reset_enabled) return '';
  const period = question.calculation_reset_period || 'weekly';
  if (period === 'weekly') {
    const name = CALC_RESET_WEEKDAYS.find(
      (row) => row.value === String(question.calculation_reset_weekday ?? 0),
    );
    return `Resets weekly on ${name ? name.label : 'Sunday'}`;
  }
  if (period === 'monthly') {
    return `Resets monthly on the ${ordinalDay(question.calculation_reset_day || 1)}`;
  }
  if (period === 'yearly') {
    const month = CALC_RESET_MONTHS.find(
      (row) => row.value === String(question.calculation_reset_month ?? 1),
    );
    const monthName = month ? month.label : 'January';
    return `Resets yearly on ${ordinalDay(question.calculation_reset_day || 1)} ${monthName}`;
  }
  return '';
}

function isoToLocalDate(iso) {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function dateToIso(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function clampLocalDate(year, month, day) {
  const last = daysInMonth(year, month - 1);
  return new Date(year, month - 1, Math.min(last, Math.max(1, day)));
}

/** Most recent reset boundary on or before ``asOfIso`` (YYYY-MM-DD). */
export function periodStartIso(asOfIso, { period, weekday = 0, day = 1, month = 1 } = {}) {
  const asOf = isoToLocalDate(asOfIso);
  if (!asOf || !period) return null;
  if (period === 'weekly') {
    const target = Number(weekday) || 0;
    const delta = (asOf.getDay() - target + 7) % 7;
    const start = new Date(asOf);
    start.setDate(asOf.getDate() - delta);
    return dateToIso(start);
  }
  if (period === 'monthly') {
    let candidate = clampLocalDate(asOf.getFullYear(), asOf.getMonth() + 1, Number(day) || 1);
    if (candidate > asOf) {
      const previous = new Date(asOf.getFullYear(), asOf.getMonth() - 1, 1);
      candidate = clampLocalDate(
        previous.getFullYear(), previous.getMonth() + 1, Number(day) || 1,
      );
    }
    return dateToIso(candidate);
  }
  if (period === 'yearly') {
    let candidate = clampLocalDate(
      asOf.getFullYear(), Number(month) || 1, Number(day) || 1,
    );
    if (candidate > asOf) {
      candidate = clampLocalDate(
        asOf.getFullYear() - 1, Number(month) || 1, Number(day) || 1,
      );
    }
    return dateToIso(candidate);
  }
  return null;
}

export function shouldResetPreviousFigures(question, asOfIso, previousOnIso) {
  if (!question?.calculation_reset_enabled) return false;
  const period = question.calculation_reset_period;
  if (!['weekly', 'monthly', 'yearly'].includes(period)) return false;
  if (!asOfIso || !previousOnIso) return false;
  const start = periodStartIso(asOfIso, {
    period,
    weekday: Number(question.calculation_reset_weekday ?? 0),
    day: Number(question.calculation_reset_day ?? 1),
    month: Number(question.calculation_reset_month ?? 1),
  });
  if (!start) return false;
  return String(previousOnIso).slice(0, 10) < start;
}

export function localIsoDate(now = new Date()) {
  return dateToIso(now);
}

export function previousSubmissionDate(questions) {
  let best = '';
  (questions || []).forEach((question) => {
    const stamp = question?.last_submission?.submitted_at;
    const iso = stamp ? String(stamp).slice(0, 10) : '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso) && iso > best) best = iso;
  });
  return best || null;
}

export function resetIsActive(question, questions) {
  if (!question || question.question_type !== 'calculation') return false;
  if (!question.calculation_reset_enabled) return false;
  if (question.calculation_reset_active === true) return true;
  if (question.calculation_reset_active === false) return false;
  return shouldResetPreviousFigures(
    question,
    localIsoDate(),
    previousSubmissionDate(questions),
  );
}

function questionsById(questions) {
  const byId = {};
  (questions || []).forEach((question) => {
    if (question && question.id != null) byId[String(question.id)] = question;
  });
  return byId;
}

export function lastAnswerIdsInClosure(question, questions) {
  const byId = questionsById(questions);
  const found = new Set();
  const seen = new Set();
  function walk(current) {
    const qid = current && current.id != null ? String(current.id) : '';
    if (qid && seen.has(qid)) return;
    if (qid) seen.add(qid);
    termQuestionIds(current).forEach((ref) => {
      const src = byId[String(ref)];
      if (!src) return;
      if (src.question_type === 'last_answer') found.add(String(ref));
      else if (src.question_type === 'calculation') walk(src);
    });
  }
  walk(question);
  return found;
}

function calculationDependsOn(question, targetId, questions, seen = new Set()) {
  const byId = questionsById(questions);
  const qid = question && question.id != null ? String(question.id) : '';
  if (!qid || seen.has(qid)) return false;
  seen.add(qid);
  return termQuestionIds(question).some((ref) => {
    if (String(ref) === String(targetId)) return true;
    const src = byId[String(ref)];
    if (src && src.question_type === 'calculation') {
      return calculationDependsOn(src, targetId, questions, seen);
    }
    return false;
  });
}

export function answersIgnoringPreviousFigures(question, answers, questions) {
  const zeroIds = lastAnswerIdsInClosure(question, questions);
  if (!zeroIds.size) return answers;
  const local = { ...(answers || {}) };
  zeroIds.forEach((id) => { local[id] = 0; });
  orderCalculations(questions).some((upstream) => {
    if (String(upstream.id) === String(question.id)) return true;
    if (!calculationDependsOn(question, upstream.id, questions)) return false;
    const formatted = formatCalculated(evaluateQuestion(upstream, local), upstream);
    local[upstream.id] = formatted == null ? '' : String(formatted);
    return false;
  });
  return local;
}

export function seedCalculationFingerprints(questions, answers) {
  const prints = {};
  (questions || []).forEach((q) => {
    if (q.question_type === 'calculation') {
      prints[q.id] = sourcesFingerprint(q, answers);
    }
  });
  return prints;
}

/**
 * Recompute calculation answers whose *source* values changed.
 *
 * Returns the same ``answers`` reference when nothing changed, so a
 * React effect can no-op without looping.
 */
export function syncCalculatedAnswers(questions, answers, prevPrints = {}, { force = false } = {}) {
  const calcs = orderCalculations(questions);
  if (!calcs.length) {
    return { answers, fingerprints: prevPrints };
  }
  const working = { ...answers };
  let changed = false;
  const fingerprints = { ...prevPrints };
  calcs.forEach((q) => {
    const resetting = resetIsActive(q, questions);
    const local = resetting
      ? answersIgnoringPreviousFigures(q, working, questions)
      : working;
    const fp = `${resetting ? '1' : '0'}|${sourcesFingerprint(q, local)}`;
    const should = force || fingerprints[q.id] !== fp;
    fingerprints[q.id] = fp;
    if (!should) return;
    const formatted = formatCalculated(evaluateQuestion(q, local), q);
    const nextVal = formatted == null ? '' : String(formatted);
    if (String(working[q.id] ?? '') !== nextVal) {
      working[q.id] = nextVal;
      changed = true;
    }
  });
  return { answers: changed ? working : answers, fingerprints };
}

export function formulaLabel(terms, questions) {
  const byId = {};
  (questions || []).forEach((q) => { byId[String(q.id)] = q.text || 'Untitled'; });
  const opMark = { '+': '+', '-': '−', '*': '×', '/': '÷' };
  return (terms || []).map((t, i) => {
    const name = t.question_id ? (byId[String(t.question_id)] || '…') : '…';
    if (i === 0) return name;
    return `${opMark[t.op] || t.op || '?'} ${name}`;
  }).join(' ');
}
